import { useUserStore } from '@/stores/user';
import { useConnectionStore } from '@/stores/connection';
import type { TokenRefreshResponse } from '@/types';

const TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000; // 5 minutes before expiry
let refreshTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Decode JWT exp claim without signature verification.
 */
function getTokenExpiry(jwt: string): number | null {
  try {
    const payload = JSON.parse(atob(jwt.split('.')[1]));
    return payload.exp ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

/**
 * Schedule a token refresh before the current JWT expires.
 */
export function scheduleTokenRefresh() {
  const userStore = useUserStore();

  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }

  const expiry = getTokenExpiry(userStore.jwt);
  if (!expiry) return;

  const now = Date.now();
  const refreshAt = expiry - TOKEN_REFRESH_BUFFER_MS;
  const delay = Math.max(refreshAt - now, 0);

  refreshTimer = setTimeout(() => {
    refreshToken();
  }, delay);
}

/**
 * Refresh the JWT via the backend.
 */
export async function refreshToken(): Promise<string | null> {
  const userStore = useUserStore();
  const connectionStore = useConnectionStore();

  try {
    const response = await fetch('/call-center/refresh-token', {
      headers: { 'X-install-id': userStore.installId },
    });

    if (!response.ok) {
      if (response.status === 401) {
        connectionStore.addAlert({
          type: 'token-expired',
          severity: 'error',
          message: 'Session expired. Please reload the page.',
          retryable: false,
        });
        return null;
      }
      throw new Error(`Refresh failed: ${response.status}`);
    }

    const data: TokenRefreshResponse = await response.json();
    userStore.updateToken(data.token);
    connectionStore.clearByType('token-expiring');

    // Schedule next refresh
    scheduleTokenRefresh();

    return data.token;
  } catch (err) {
    connectionStore.addAlert({
      type: 'token-expiring',
      severity: 'warning',
      message: 'Failed to refresh session. Retrying...',
      retryable: true,
    });

    // Retry in 30 seconds
    refreshTimer = setTimeout(() => refreshToken(), 30_000);
    return null;
  }
}

/**
 * Stop the refresh timer (on unmount).
 */
export function stopTokenRefresh() {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}
